import Phaser from 'phaser';
import { ANIMATION } from '@/config/balance.config';

/**
 * Animator gracza — bogaty zestaw proceduralnych efektów, bez bob'a (kamera
 * follow'uje gracza i bob = jitter na ekranie).
 *
 * Stosuje:
 *  - Idle breath (skala) — stała pulsacja
 *  - Walking tilt — pochył w stronę ruchu (smooth-damp)
 *  - Mirror flip — postać patrzy w stronę ruchu
 *  - **Step sway** — kołysanie scaleX z częstotliwością chodzenia (~4 Hz)
 *    symuluje przechodzenie z nogi na nogę bez spritesheet'a
 *  - **Hop punch** — przy starcie ruchu krótkie rozciągnięcie pionowe (scaleY)
 *  - **Stop squish** — przy zatrzymaniu krótkie ściśnięcie pionowe
 *  - **Dust trail** — drobne kurzowe cząstki z tyłu gdy biegnie (speed > threshold)
 *  - **Idle fidget** — co kilka sekund bez ruchu, delikatny pochył w bok
 *  - Shadow — eliptyczny placek pod stopami
 */
export class PlayerAnimator {
  readonly shadow: Phaser.GameObjects.Ellipse;

  private baseScale: number;

  /** Aktualizuje bazową skalę (po live-swapie skina avatara o innym rozmiarze źródła). */
  setBaseScale(scale: number): void {
    this.baseScale = scale;
  }

  // Bieżące wartości (lerp'owane)
  private smoothedVx = 0;
  private smoothedVy = 0;
  private currentTilt = 0;

  // Hop / Stop pulses (one-shot, lerp do 1.0)
  private hopScaleY = 1;
  private stopScaleY = 1;

  // State tracking
  private wasMoving = false;
  private idleStartedAt = 0;

  // Dust trail timing
  private lastDustEmitAt = 0;

  // Idle fidget
  private nextFidgetAt: number;
  private fidgetActiveUntil = 0;
  private fidgetDirection = 1;          // +1 prawo, -1 lewo

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly sprite: Phaser.GameObjects.Image,
    baseScale: number,
    initialX: number,
    initialY: number,
  ) {
    this.baseScale = baseScale;
    this.idleStartedAt = scene.time.now;
    this.nextFidgetAt = scene.time.now + this.randomFidgetDelay();

    const sh = ANIMATION.shadow;
    this.shadow = scene.add.ellipse(
      initialX,
      initialY,                              // gracz ma origin bottom-center → Y == feet
      sh.width + 12,
      sh.height + 3,
      0x000000,
      sh.alpha,
    );
    this.shadow.setDepth(this.sprite.depth - 1);
  }

  /** Aktualizuje wszystkie efekty wizualne. Nie modyfikuje sprite.x/y. */
  update(time: number, vx: number, vy: number, spriteX: number, spriteY: number): void {
    const petCfg = ANIMATION.pet;
    const cfg = ANIMATION.player;

    // 1) Smooth velocity (lowpass) — dla płynnego tilt'u
    this.smoothedVx = this.smoothedVx * petCfg.velocitySmoothing + vx * (1 - petCfg.velocitySmoothing);
    this.smoothedVy = this.smoothedVy * petCfg.velocitySmoothing + vy * (1 - petCfg.velocitySmoothing);
    const speed = Math.hypot(this.smoothedVx, this.smoothedVy);
    const moving = speed > cfg.movingSpeedThreshold;

    // 2) Wykryj transition idle ↔ moving — trigger hop / stop pulses
    if (moving && !this.wasMoving) {
      // Start: hop punch
      this.hopScaleY = cfg.hopOnStart.scaleY;
      this.idleStartedAt = -1;
    } else if (!moving && this.wasMoving) {
      // Stop: squish
      this.stopScaleY = cfg.stopSquish.scaleY;
      this.idleStartedAt = time;
      this.nextFidgetAt = time + this.randomFidgetDelay();
    }
    this.wasMoving = moving;

    // 3) Hop/Stop pulses — lerp do 1.0 (te same wartości peak'u używamy jako "amount")
    // Dłuższe pulses używają mniejszego lerp factor żeby trwały dłużej
    const hopLerp = (16 / cfg.hopOnStart.durationMs) * 4;     // ~0.27 dla 240ms
    const stopLerp = (16 / cfg.stopSquish.durationMs) * 4;
    this.hopScaleY += (1 - this.hopScaleY) * hopLerp;
    this.stopScaleY += (1 - this.stopScaleY) * stopLerp;

    // 4) Breath (idle)
    const t = time / 1000;
    const breath = 1 + Math.sin(t * Math.PI * 2 * petCfg.breathFrequencyHz) * petCfg.breathAmplitude;

    // 5) Step sway — scaleX oscillation gdy chodzi (symuluje kroki)
    let stepSway = 1;
    if (moving) {
      stepSway = 1 + Math.sin(t * Math.PI * 2 * cfg.stepSwayFrequencyHz) * cfg.stepSwayAmplitude;
    }

    // 6) Tilt — pochył w stronę ruchu
    const targetTilt = moving
      ? Math.max(-1, Math.min(1, this.smoothedVx / 300)) * petCfg.maxTiltRad
      : 0;
    this.currentTilt += (targetTilt - this.currentTilt) * petCfg.tiltLerp;

    // 7) Mirror flip — gracz "patrzy" w stronę ruchu
    if (this.smoothedVx > 30) this.sprite.flipX = false;
    else if (this.smoothedVx < -30) this.sprite.flipX = true;

    // 8) Idle fidget — po N sekundach bez ruchu delikatny pochył w bok
    let fidgetTilt = 0;
    if (!moving && this.idleStartedAt > 0 && time >= this.nextFidgetAt) {
      if (this.fidgetActiveUntil <= 0) {
        // Start fidget'a
        this.fidgetActiveUntil = time + cfg.idleFidget.durationMs;
        this.fidgetDirection = Math.random() < 0.5 ? -1 : 1;
      }
      if (time < this.fidgetActiveUntil) {
        const fidgetT = 1 - (this.fidgetActiveUntil - time) / cfg.idleFidget.durationMs;
        // Krzywa: sin(π*t) → 0 na początku, peak w środku, 0 na końcu
        fidgetTilt = Math.sin(fidgetT * Math.PI) * cfg.idleFidget.tiltRad * this.fidgetDirection;
      } else {
        this.fidgetActiveUntil = 0;
        this.nextFidgetAt = time + this.randomFidgetDelay();
      }
    }

    // 9) Aplikuj rotation + scale (combine all)
    this.sprite.rotation = this.currentTilt + fidgetTilt;
    this.sprite.scaleX = this.baseScale * breath * stepSway;
    this.sprite.scaleY = this.baseScale * breath * this.hopScaleY * this.stopScaleY;

    // 10) Dust trail — emituj kurz gdy szybko biegnie
    if (speed > cfg.dustTrail.speedThreshold && time - this.lastDustEmitAt > cfg.dustTrail.emitIntervalMs) {
      this.emitDust(spriteX, spriteY);
      this.lastDustEmitAt = time;
    }

    // 11) Cień — bottom-center sprite'a (origin 0.5,1.0 → spriteY to stopy).
    // Depth synchronizowany co klatkę (sprite.depth - 1): w scenach z y-sortem
    // (PlazaScene ustawia sprite.depth = y co klatkę) cień musi iść ZA sprite'em
    // niezależnie od pozycji, inaczej zostaje pod dekoracjami (Codex 5.B.4b-1 LOW).
    // W WorldScene sprite ma stały depth → no-op.
    this.shadow.x = spriteX;
    this.shadow.y = spriteY - 2;
    this.shadow.setDepth(this.sprite.depth - 1);
  }

  private emitDust(playerX: number, playerY: number): void {
    if (!this.scene.textures.exists('fx-particle-grey')) return;
    const dt = ANIMATION.player.dustTrail;

    // Spawn point: za graczem (w kierunku przeciwnym do velocity), na wysokości stóp
    const speed = Math.hypot(this.smoothedVx, this.smoothedVy);
    const dirX = speed > 0 ? -this.smoothedVx / speed : 0;
    const dirY = speed > 0 ? -this.smoothedVy / speed : 0;
    const spawnX = playerX + dirX * dt.offsetBehindPx;
    const spawnY = playerY - 4 + dirY * dt.offsetBehindPx * 0.3;     // -4 = lekko nad stopami

    const emitter = this.scene.add.particles(spawnX, spawnY, 'fx-particle-grey', {
      speed: { min: 10, max: 30 },
      lifespan: dt.lifespanMs,
      scale: { start: dt.scaleStart, end: dt.scaleEnd },
      quantity: 1,
      gravityY: dt.gravityY,
      angle: { min: 70, max: 110 },        // emisja w dół + na boki
      tint: dt.tint,
      alpha: { start: 0.6, end: 0 },
      emitting: false,
    });
    emitter.setDepth(dt.depth);
    emitter.explode(1);
    this.scene.time.delayedCall(dt.lifespanMs + 100, () => emitter.destroy());
  }

  private randomFidgetDelay(): number {
    const cfg = ANIMATION.player.idleFidget;
    const min = cfg.everySecMin * 1000;
    const max = cfg.everySecMax * 1000;
    return min + Math.random() * (max - min);
  }

  destroy(): void {
    this.shadow.destroy();
  }
}
